import { baseApi } from '@/services/api';
import {
  Event,
  ChatSession,
  ChatRequest,
  ChatResponse,
  PaymentIntent,
  CreatePaymentIntentRequest,
} from './mainTypes';

export const mainAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ===== EVENTS =====
    getAllEvents: builder.query<Event[], void>({
      query: () => ({
        url: '/events',
        method: 'GET',
      }),
      // ✅ Transform response to extract events array
      transformResponse: (response: { events: Event[] } | Event[]) => {
        // Handle both formats
        if (Array.isArray(response)) {
          return response;
        }
        return response.events || [];
      },
      providesTags: ['Events'],
    }),

    getEvent: builder.query<Event, string>({
      query: (eventId) => ({
        url: `/events/${eventId}`,
        method: 'GET',
      }),
      // ✅ Transform response
      transformResponse: (response: { event: Event } | Event) => {
        if ('event' in response) {
          return response.event;
        }
        return response as Event;
      },
      providesTags: (result, error, id) => [{ type: 'Events', id }],
    }),

    createEvent: builder.mutation<Event, Partial<Event>>({
      query: (data) => ({
        url: '/events',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Events'],
    }),

    updateEvent: builder.mutation<Event, { eventId: string; updates: Partial<Event> }>({
      query: ({ eventId, updates }) => ({
        url: `/events/${eventId}`,
        method: 'PUT',
        data: updates,
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'Events', id: eventId }],
    }),

    deleteEvent: builder.mutation<{ message: string }, string>({
      query: (eventId) => ({
        url: `/events/${eventId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Events'],
    }),

    // ===== CHAT =====
    getChatSessions: builder.query<ChatSession[], void>({
      query: () => ({
        url: '/chat/sessions',
        method: 'GET',
      }),
      providesTags: ['ChatSessions'],
    }),

    getChatSession: builder.query<ChatSession, string>({
      query: (sessionId) => ({
        url: `/chat/sessions/${sessionId}`,
        method: 'GET',
      }),
      transformResponse: (response: { session: ChatSession } | ChatSession) => {
        if ('session' in response) {
          return response.session;
        }
        return response as ChatSession;
      },
      providesTags: (result, error, id) => [{ type: 'ChatSessions', id }],
    }),

    sendChatMessage: builder.mutation<ChatResponse, ChatRequest>({
      query: (data) => ({
        url: '/chat',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['ChatSessions'],
    }),

    deleteChatSession: builder.mutation<{ message: string }, string>({
      query: (sessionId) => ({
        url: `/chat/sessions/${sessionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ChatSessions'],
    }),

    // ===== PAYMENTS =====
    createPaymentIntent: builder.mutation<PaymentIntent, CreatePaymentIntentRequest>({
      query: (data) => ({
        url: '/payments/create-intent',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PaymentIntents'],
    }),

    getPaymentIntent: builder.query<PaymentIntent, string>({
      query: (paymentIntentId) => ({
        url: `/payments/intent/${paymentIntentId}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'PaymentIntents', id }],
    }),

    confirmPayment: builder.mutation<PaymentIntent, { paymentIntentId: string; paymentMethodId: string }>({
      query: ({ paymentIntentId, paymentMethodId }) => ({
        url: `/payments/confirm/${paymentIntentId}`,
        method: 'POST',
        data: { payment_method_id: paymentMethodId },
      }),
      invalidatesTags: (result, error, { paymentIntentId }) => [
        { type: 'PaymentIntents', id: paymentIntentId },
        'Bookings',
      ],
    }),
  }),
});

export const {
  // Events
  useGetAllEventsQuery,
  useLazyGetAllEventsQuery,
  useGetEventQuery,
  useLazyGetEventQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  
  // Chat
  useGetChatSessionsQuery,
  useLazyGetChatSessionsQuery,
  useGetChatSessionQuery,
  useLazyGetChatSessionQuery,
  useSendChatMessageMutation,
  useDeleteChatSessionMutation,
  
  // Payments
  useCreatePaymentIntentMutation,
  useGetPaymentIntentQuery,
  useLazyGetPaymentIntentQuery,
  useConfirmPaymentMutation,
} = mainAPI;
